/**
 * Telemetry HTTP client — uses `fetch` directly (NOT the configured axios
 * instance) so telemetry calls cannot recursively trigger their own
 * `api_call` timing through the interceptor. Two-line side effects also
 * remove a refresh-token-redirect concern: the /telemetry/* endpoints use
 * `optionalAuth` and gracefully accept anonymous reports, so a 401 would
 * never fire here.
 *
 * Errors are swallowed throughout. Telemetry must never break the host
 * app; if reporting fails, drop the event silently.
 */

import { API_URL } from '@/lib/api'

interface CommonContext {
  app_version: string
  os_name: 'web'
}

const baseContext: CommonContext = {
  app_version: import.meta.env.VITE_APP_VERSION ?? 'web-admin',
  os_name: 'web',
}

async function post(path: string, body: unknown): Promise<Response | null> {
  try {
    return await fetch(API_URL + path, {
      method: 'POST',
      credentials: 'include', // attach refresh cookie if present
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      // keepalive lets the event fire during `beforeunload` even when the
      // tab is being torn down — critical for session-end and unhandled
      // navigation crashes.
      keepalive: true,
    })
  } catch { return null }
}

/** Returns the server-assigned (or echoed) session_token. */
export async function postSessionStart(token: string, deviceModel: string): Promise<string> {
  const body = {
    session_token: token,
    device_model: deviceModel,
    ...baseContext,
    os_version: navigator.userAgent.slice(0, 32),
  }
  const resp = await post('/telemetry/sessions', body)
  if (resp && resp.ok) {
    try {
      const data = await resp.json()
      const echo = data?.data?.session_token
      if (typeof echo === 'string' && echo.length > 0) return echo
    } catch { /* fall through */ }
  }
  return token
}

/** Returns true when the call is "done" — server accepted (2xx) or
 *  rejected with a 4xx (retrying won't help: bad token, unknown session,
 *  etc.). Returns false on network failure or 5xx so the caller can keep
 *  the token on its retry queue and try again on the next boot. */
export async function postSessionEnd(token: string): Promise<boolean> {
  const resp = await post(`/telemetry/sessions/${encodeURIComponent(token)}/end`, {})
  if (!resp) return false
  if (resp.status >= 500) return false
  return true
}

export async function postCrash(args: {
  exception: string
  stack_trace: string
  route?: string
  device_model?: string
}): Promise<void> {
  await post('/telemetry/crashes', { ...baseContext, ...args })
}

export async function postPerfBatch(events: Array<{
  kind: 'screen_load' | 'api_call' | 'app_start'
  name: string
  duration_ms: number
  status_code?: number
}>): Promise<void> {
  if (events.length === 0) return
  const stamped = events.map((e) => ({ ...baseContext, ...e }))
  await post('/telemetry/performance', { events: stamped })
}

export async function postError(args: {
  level: 'warn' | 'error'
  message: string
  route?: string
  context?: Record<string, unknown>
}): Promise<void> {
  await post('/telemetry/errors', { ...baseContext, ...args })
}
